import React, { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { useAudioRecorderContext } from "@/hooks/audio-recorder-context";
import { Button } from "@/components/ui/button";
import { Square } from "lucide-react";

const MIC_STOP_DELAY_MS = 250;

export type MicButtonProps = {
  label?: string;
  onRecordStarted?: () => void;
  onRecordEnded?: () => void;
  onAudioAvailable?: (blob: Blob) => void;
  onAudioChunkAvailable?: (chunk: Uint8Array) => void; // For streaming audio chunks
  onErrorMessage?: (message: string, duration?: number) => void;
  isRecording?: boolean; // Allow parent to access recording state
  visualType?: 'bars' | 'line';
} & React.ComponentProps<"button">;

// Waveform component embedded directly in the MicButton component
interface WaveformProps {
  isRecording: boolean;
  className?: string;
  visualType?: 'bars' | 'line';
}

const WaveformEmbedded: React.FC<WaveformProps> = ({ isRecording, className = '', visualType = 'bars' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const bufferLengthRef = useRef<number>(0);
  // Store the time-based data for scrolling visualization
  const timeDataRef = useRef<number[]>([]);
  const timeDataMaxLength = useRef(50); // Maximum number of data points to store, reduced for faster movement

  const { getAnalyser } = useAudioRecorderContext();
  
  useEffect(() => {
    if (!isRecording || !canvasRef.current) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      return;
    }

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get the analyser from the context
    const analyser = getAnalyser();
    if (!analyser) {
      // If analyser is not available, fallback to a simple animation
      return;
    }

    analyserRef.current = analyser;

    // Set up the analyser
    analyser.fftSize = 256;
    const bufferLength = analyser.frequencyBinCount;
    bufferLengthRef.current = bufferLength;
    const dataArray = new Uint8Array(bufferLength);
    dataArrayRef.current = dataArray;

    // Initialize the time data array
    timeDataRef.current = Array(bufferLength).fill(0);

    const draw = () => {
      if (!ctx || !analyserRef.current || !dataArrayRef.current) return;

      // Set canvas dimensions to match its display size
      const displayWidth = canvas.clientWidth;
      const displayHeight = canvas.clientHeight;

      if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
      }

      // Get the current audio data
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);

      if (visualType === 'bars') {
        // Draw a scrolling bar visualization
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Get peak of frequency data for current amplitude (more responsive than average)
        let max = 0;
        for (let i = 0; i < bufferLengthRef.current; i++) {
          if (dataArrayRef.current![i] > max) {
            max = dataArrayRef.current![i];
          }
        }
        
        // Add the current amplitude to the time-based data
        timeDataRef.current.push(max);
        if (timeDataRef.current.length > timeDataMaxLength.current) {
          timeDataRef.current.shift(); // Remove oldest value to maintain size
        }

        // Draw the scrolling bars
        const barWidth = displayWidth / timeDataMaxLength.current;
        ctx.fillStyle = 'white'; // white color

        for (let i = 0; i < timeDataRef.current.length; i++) {
          const barHeight = (timeDataRef.current[i] / 255) * displayHeight; // Scale bar height based on data
          // Calculate x position to make it move from right to left
          const x = displayWidth - (timeDataRef.current.length - i) * barWidth;

          ctx.fillRect(
            x,
            displayHeight - barHeight, // Start from bottom
            barWidth - 1, // Add some spacing between bars
            barHeight
          );
        }
      } else if (visualType === 'line') {
        // Draw a scrolling line visualization
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Get peak of frequency data for current amplitude (more responsive than average)
        let max = 0;
        for (let i = 0; i < bufferLengthRef.current; i++) {
          if (dataArrayRef.current![i] > max) {
            max = dataArrayRef.current![i];
          }
        }
        
        // Add the current amplitude to the time-based data
        timeDataRef.current.push(max);
        if (timeDataRef.current.length > timeDataMaxLength.current) {
          timeDataRef.current.shift(); // Remove oldest value to maintain size
        }

        // Draw the scrolling line
        ctx.beginPath();
        ctx.strokeStyle = 'white'; // white color
        ctx.lineWidth = 2;

        const pointSpacing = displayWidth / timeDataMaxLength.current;
        
        for (let i = 0; i < timeDataRef.current.length; i++) {
          // Normalize the data to -1 to 1 range (from 0-255 to -1 to 1)
          const v = (timeDataRef.current[i] - 128) / 128; 
          const y = displayHeight / 2 - (v * (displayHeight / 2)); // Center line with amplitude
          // Calculate x position based on current position and spacing
          const x = displayWidth - (timeDataRef.current.length - i) * pointSpacing;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.stroke();
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    animationRef.current = requestAnimationFrame(draw);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isRecording, getAnalyser, visualType]);

  // When not recording, show a simple horizontal line
  if (!isRecording) {
    return (
      <div className={`w-full h-8 flex items-center ${className}`}>
        <div className="w-full h-0.5 bg-white"></div>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={`w-full h-8 ${className}`}
    />
  );
};

export const MicButton: React.FC<MicButtonProps> = ({ className, 
  label,
  onRecordStarted,
  onRecordEnded,
  onAudioAvailable,
  onAudioChunkAvailable,
  onErrorMessage,
  isRecording: externalIsRecording,
  visualType = 'bars',
  ...props }) => {
  const { isRecording: internalIsRecording, startRecording, stopRecording, isInitializing, addRecordingCompleteListener, addErrorListener, addAudioChunkListener } =
    useAudioRecorderContext();
    
  const isRecording = externalIsRecording !== undefined ? externalIsRecording : internalIsRecording;
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [timerLabel, setTimerLabel] = useState("00:00");
  
  const stopTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Subscribe to recording completion and chunk events for this button instance
  useEffect(() => {
    const unsubscribeComplete = addRecordingCompleteListener((blob) => {
      setAudioBlob(blob);
      if (onAudioAvailable) {
        onAudioAvailable(blob);
      }
    });
    
    const unsubscribeError = addErrorListener((message) => {
      if (onErrorMessage) {
        onErrorMessage(message);
      }
    });
    
    // Subscribe to chunk listener if provided
    let unsubscribeChunk: (() => void) | null = null;
    if (onAudioChunkAvailable) {
      unsubscribeChunk = addAudioChunkListener((chunk) => {
        onAudioChunkAvailable(chunk);
      });
    }

    // Cleanup listeners on unmount
    return () => {
      unsubscribeComplete();
      unsubscribeError();
      if (unsubscribeChunk) {
        unsubscribeChunk();
      }
    };
  }, [addRecordingCompleteListener, addErrorListener, addAudioChunkListener, onAudioAvailable, onAudioChunkAvailable, onErrorMessage]);

  // Update timer when recording starts/stops
  useEffect(() => {
    if (isRecording) {
      startTimeRef.current = Date.now();
      intervalRef.current = setInterval(() => {
        const seconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const mins = String(Math.floor(seconds / 60)).padStart(2, "0");
        const secs = String(seconds % 60).padStart(2, "0");
        setTimerLabel(`${mins}:${secs}`);
      }, 1000);
    } else {
      // Clear interval when not recording
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setTimerLabel("00:00");
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isRecording]);

  useEffect(() => {
    return () => {
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handlePress = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
      return; 
    }
    if (!isRecording && !isInitializing) {
      if(onRecordStarted) 
        onRecordStarted();
      startRecording();
    }
  }, [isRecording, isInitializing, onRecordStarted, startRecording]);

  const handleRelease = useCallback(() => {
    if (isRecording) {
      stopTimerRef.current = setTimeout(() => {
        stopRecording();
        if(onRecordEnded) 
          onRecordEnded();
        setTimerLabel("00:00");
        stopTimerRef.current = null;
      }, MIC_STOP_DELAY_MS);
    }
  }, [isRecording, onRecordEnded, stopRecording]);

  // Prevent context menu on long press
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  // Handle pointer events for better touch/mouse unification
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    // Only respond to primary pointer (left mouse button or first touch)
    if (e.isPrimary) {
      e.preventDefault();
      handlePress();
    }
  }, [handlePress]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    // Only respond to primary pointer
    if (e.isPrimary) {
      e.preventDefault();
      handleRelease();
    }
  }, [handleRelease]);

  const handlePointerLeave = useCallback((e: React.PointerEvent) => {
    // Treat pointer leaving the button as release
    if (e.isPrimary) {
      handleRelease();
    }
  }, [handleRelease]);

  const handlePointerCancel = useCallback((e: React.PointerEvent) => {
    // Handle pointer being canceled (e.g., system gesture)
    if (e.isPrimary) {
      handleRelease();
    }
  }, [handleRelease]);

  return (
    <Button
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
      onPointerCancel={handlePointerCancel}
      onContextMenu={handleContextMenu}
      disabled={isInitializing}
      variant="default"
      className={cn(
        "select-none touch-none w-auto min-w-[140px] px-6 h-12 relative overflow-hidden rounded-full font-medium transition-all duration-200",
        isRecording
          ? "bg-gradient-to-br from-red-400 to-pink-500 hover:from-red-500 hover:to-pink-600 shadow-lg shadow-red-200/40 scale-105"
          : "bg-gradient-to-br from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black shadow-lg shadow-gray-400/30 hover:scale-105",
        "border-2 border-white/60 text-white",
        className
      )}
      style={{
        touchAction: 'none', // Prevent default touch behaviors
        userSelect: 'none',  // Prevent text selection
        WebkitUserSelect: 'none', // Safari
        WebkitTouchCallout: 'none' // Prevent iOS callout
      }}
      {...props}
    >
      {/* Waveform background when recording */}
      {isRecording && (
        <div className="absolute inset-0 flex items-center justify-center opacity-60">
          <div className="w-full h-full">
            <WaveformEmbedded isRecording={isRecording} className="w-full h-full" visualType={visualType} />
          </div>
        </div>
      )}

      {/* Content overlay */}
      <div className="relative z-10 text-white">
        {isRecording ? (
          <div className="flex items-center justify-center gap-2 w-full h-full">
            <Square className="h-4 w-4 fill-current animate-pulse" />
            <span className="font-mono text-sm font-semibold">{timerLabel}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center w-full h-full">
            {(label || "").split('\n').map((line, index) => (
              <span key={index} className="text-xs font-semibold tracking-wide uppercase">{line}</span>
            ))}
          </div>
        )}
      </div>
    </Button>
  );
};